import type { ManualSubscriptionDto } from '@nomiss/sdk';
import {
  ApiRequestError,
  executeSdkEnvelopeRequest,
  executeSdkRawRequest,
} from './api-request.util';
import { adminUsersApi } from './sdk-client';

export class AdminUsersError extends ApiRequestError {
  constructor(
    status: number,
    message: string,
    name = 'AdminUsersError',
    errorCode?: string,
  ) {
    if (errorCode) {
      super(status, message, name, errorCode);
    } else {
      super(status, message, name);
    }
  }
}

export type AdminUserStatusFilter = 'active' | 'blocked' | 'deleted';
export type AdminUserTierFilter = 'free' | 'pro' | 'champion';

export const ASSIGNABLE_ADMIN_PLAN_CODES = ['free', 'champion_yearly'] as const;
export type AssignableAdminPlanCode = (typeof ASSIGNABLE_ADMIN_PLAN_CODES)[number];

export function isAssignableAdminPlanCode(
  planCode: string | null | undefined,
): planCode is AssignableAdminPlanCode {
  return planCode === 'free' || planCode === 'champion_yearly';
}

export type AdminUsersListParams = {
  search?: string;
  status?: AdminUserStatusFilter;
  tier?: AdminUserTierFilter;
  page?: number;
  limit?: number;
};

export type AdminUserListItem = {
  id: string;
  email: string | null;
  phone: string;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  deletedAt: string | null;
  createdAt: string;
  roles: string[];
  tier: string | null;
  planCode: string | null;
  provider: string | null;
};

export type AdminUsersListMeta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export type AdminUsersListResult = {
  data: AdminUserListItem[];
  meta: AdminUsersListMeta;
};

export type AdminUserDetail = {
  user: {
    id: string;
    email: string | null;
    phone: string;
    firstName: string | null;
    lastName: string | null;
    isActive: boolean;
    isEmailVerified: boolean;
    deletedAt: string | null;
    createdAt: string;
    updatedAt: string;
    roles: string[];
  };
  follows: {
    sports: Array<{
      id: string;
      name: string;
      displayOrder: number | null;
      sections: Array<{
        id: string;
        label: string;
        items: Array<{ id: string; name: string }>;
      }>;
    }>;
  };
  settings: {
    digestTime: string | null;
    championDigestTime: string | null;
    language: string | null;
    timezone: string | null;
    spoilerEnabled: boolean;
    notificationEnabled: boolean;
  } | null;
  subscription: {
    planCode: string;
    tier: string;
    status: string;
    provider: string;
    expiresAt: string | null;
    autoRenew: boolean;
  } | null;
  referrals: {
    code: string | null;
    asReferrer: {
      total: number;
      completed: number;
      proMonthsEarned: number;
      maxProMonths: number;
    };
    asReferee: {
      redeemed: boolean;
      redeemedAt: string | null;
      redeemedCode: string | null;
    };
    credit: {
      status: 'none' | 'pending' | 'active' | 'used';
      activatedAt: string | null;
      expiresAt: string | null;
    };
  };
};

export type OverrideAdminSubscriptionResult = {
  success: boolean;
  planCode: string;
  tier: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readStringField(
  record: Record<string, unknown>,
  camelKey: string,
  snakeKey: string,
): string | null {
  const value = record[camelKey] ?? record[snakeKey];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readBooleanField(
  record: Record<string, unknown>,
  camelKey: string,
  snakeKey: string,
  fallback: boolean,
): boolean {
  const value = record[camelKey] ?? record[snakeKey];
  return typeof value === 'boolean' ? value : fallback;
}

function normalizeAdminUserSettings(
  settings: unknown,
): AdminUserDetail['settings'] {
  if (!isRecord(settings)) return null;

  return {
    digestTime: readStringField(settings, 'digestTime', 'digest_time'),
    championDigestTime: readStringField(
      settings,
      'championDigestTime',
      'champion_digest_time',
    ),
    language: readStringField(settings, 'language', 'language'),
    timezone: readStringField(settings, 'timezone', 'timezone'),
    spoilerEnabled: readBooleanField(
      settings,
      'spoilerEnabled',
      'spoiler_enabled',
      false,
    ),
    notificationEnabled: readBooleanField(
      settings,
      'notificationEnabled',
      'notification_enabled',
      false,
    ),
  };
}

function normalizeAdminUserDetail(raw: unknown): AdminUserDetail {
  const detail = (isRecord(raw) ? raw : {}) as AdminUserDetail;
  return {
    ...detail,
    settings: normalizeAdminUserSettings(
      isRecord(raw) ? raw.settings ?? raw['settings'] : null,
    ),
  };
}

function parseListMeta(value: unknown): AdminUsersListMeta {
  if (!isRecord(value)) {
    return { page: 1, limit: 20, total: 0, totalPages: 0 };
  }

  return {
    page: typeof value.page === 'number' ? value.page : 1,
    limit: typeof value.limit === 'number' ? value.limit : 20,
    total: typeof value.total === 'number' ? value.total : 0,
    totalPages: typeof value.totalPages === 'number' ? value.totalPages : 0,
  };
}

export async function listAdminUsers(
  params: AdminUsersListParams = {},
): Promise<AdminUsersListResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminUsersApi.adminUsersControllerListUsersV1Raw({
        ...(params.search ? { search: params.search } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.tier ? { tier: params.tier } : {}),
        ...(params.page != null ? { page: params.page } : {}),
        ...(params.limit != null ? { limit: params.limit } : {}),
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      return {
        data: Array.isArray(envelope.data)
          ? (envelope.data as AdminUserListItem[])
          : [],
        meta: parseListMeta(envelope.meta),
      };
    },
    fallback: 'Failed to load users',
    ErrorClass: AdminUsersError,
  });
}

export async function getAdminUser(id: string): Promise<AdminUserDetail> {
  const detail = await executeSdkRawRequest<unknown>({
    request: () =>
      adminUsersApi.adminUsersControllerGetUserDetailV1Raw({ id }),
    fallback: 'Failed to load user detail',
    ErrorClass: AdminUsersError,
  });
  return normalizeAdminUserDetail(detail);
}

export async function blockAdminUser(
  id: string,
  blocked: boolean,
): Promise<{ message: string }> {
  return executeSdkRawRequest({
    request: () =>
      adminUsersApi.adminUsersControllerBlockUserV1Raw({
        id,
        blockUserDto: { blocked },
      }),
    fallback: blocked ? 'Failed to block user' : 'Failed to unblock user',
    ErrorClass: AdminUsersError,
  });
}

export async function overrideAdminUserSubscription(
  id: string,
  dto: ManualSubscriptionDto,
): Promise<OverrideAdminSubscriptionResult> {
  return executeSdkRawRequest({
    request: () =>
      adminUsersApi.adminUsersControllerOverrideSubscriptionV1Raw({
        id,
        manualSubscriptionDto: dto,
      }),
    fallback: 'Failed to override subscription',
    ErrorClass: AdminUsersError,
  });
}

export type CreateAdminUserInput = {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone: string;
  roles: string[];
};

export type CreateAdminUserResult = {
  message: string;
  id?: string;
};

export async function createAdminUser(
  input: CreateAdminUserInput,
): Promise<CreateAdminUserResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminUsersApi.adminUsersControllerCreateAdminUserV1Raw({
        createAdminUserDto: {
          ...input,
          roles: input.roles as any,
        },
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const data = isRecord(envelope.data) ? envelope.data : {};
      return {
        message:
          typeof envelope.message === 'string'
            ? envelope.message
            : 'Admin user created',
        id: typeof data.id === 'string' ? data.id : undefined,
      };
    },
    fallback: 'Failed to create admin user',
    ErrorClass: AdminUsersError,
  });
}
